import React, { createContext, FC, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_assignments_Assignment,
  GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_printTemplates_PrintHeadingPrintTemplate,
  GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_printTemplates_PrintHeadingPrintTemplate_printTemplate_PrintTemplate_articleTemplates_ArticleTemplate,
} from '../../__generated__/queries-topic';
import { useGetPrintHeadingById } from '../../apollo/queries/printHeading.topic.graphql';
import { AppToolbar } from '../../components/AppToolbar';
import { CircularLoader } from '../../components/Loader/Circular';
import { useStyles } from '../../pages/MultiSubjectAuto/styles';
import { paths, replaceParams } from '../../routing';
import { MultiSubjectAutoForm } from './Form';
import { MultiSubjectHeader } from './Header';

const localShowPreview = localStorage.getItem('showAutoPreview');

interface MatchParams {
  printHeadingId: string;
  originId?: string;
}

interface ContextProps {
  assignments: GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_assignments_Assignment[];
  setAssignments: React.Dispatch<
    React.SetStateAction<
      GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_assignments_Assignment[]
    >
  >;
  vacantAssignments: GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_assignments_Assignment[];
  setVacantAssignments: React.Dispatch<
    React.SetStateAction<
      GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_assignments_Assignment[]
    >
  >;
  printTemplates: GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_printTemplates_PrintHeadingPrintTemplate[];
  setPrintTemplates: React.Dispatch<
    React.SetStateAction<
      GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_printTemplates_PrintHeadingPrintTemplate[]
    >
  >;
  selectedPrintTemplateId: string;
  setSelectedPrintTemplateId: React.Dispatch<React.SetStateAction<string>>;
  articleTemplates: GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_printTemplates_PrintHeadingPrintTemplate_printTemplate_PrintTemplate_articleTemplates_ArticleTemplate[];
  setArticleTemplates: React.Dispatch<
    React.SetStateAction<
      GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_printTemplates_PrintHeadingPrintTemplate_printTemplate_PrintTemplate_articleTemplates_ArticleTemplate[]
    >
  >;
  hoveredArticleTopic: string;
  setHoveredArticleTopic: React.Dispatch<React.SetStateAction<string>>;
  showPreview: boolean;
  setShowPreview: React.Dispatch<React.SetStateAction<boolean>>;
  togglePreview: () => void;
  setPreviewResponseTime: React.Dispatch<React.SetStateAction<string>>;
  previewResponseTime: string;
  isPreviewLoading: boolean;
  setIsPreviewLoading: React.Dispatch<React.SetStateAction<boolean>>;
  getAssignment: (
    id: string,
  ) =>
    | GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_assignments_Assignment
    | undefined;
  getArticleTemplate: (
    id: string,
  ) =>
    | GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_printTemplates_PrintHeadingPrintTemplate_printTemplate_PrintTemplate_articleTemplates_ArticleTemplate
    | undefined;
}

export const MultiSubjectAutoContext = createContext({} as ContextProps);

export const MultiSubjectAutoWrapper: FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const match = useRouteMatch<MatchParams>();
  const subjectSourceId = match.params.originId;
  const { printHeadingId } = match.params;
  const { loading, data, refetch } = useGetPrintHeadingById({
    id: printHeadingId,
  });
  const [assignments, setAssignments] = useState<
    GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_assignments_Assignment[]
  >([]);
  const [vacantAssignments, setVacantAssignments] = useState<
    GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_assignments_Assignment[]
  >([]);
  const [printTemplates, setPrintTemplates] = useState<
    GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_printTemplates_PrintHeadingPrintTemplate[]
  >([]);
  const [selectedPrintTemplateId, setSelectedPrintTemplateId] = useState('');
  const [articleTemplates, setArticleTemplates] = useState<
    GetPrintHeadingByIdQuery_query_printHeading_PrintHeading_printTemplates_PrintHeadingPrintTemplate_printTemplate_PrintTemplate_articleTemplates_ArticleTemplate[]
  >([]);
  const [hoveredArticleTopic, setHoveredArticleTopic] = useState<string>('');
  const [showPreview, setShowPreview] = useState(
    !(localShowPreview === 'false'),
  );
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewResponseTime, setPreviewResponseTime] = useState('0');

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!loading) {
      setAssignments(data?.printHeading?.assignments || []);
      const templates = data?.printHeading?.printTemplates || [];
      if (!templates.length)
        return history.push({ pathname: paths.SUBJECT_LIST });
      const template = templates.find((item) => item.active);
      setPrintTemplates(templates);
      setSelectedPrintTemplateId(template?.id || templates[0].id);
    }
  }, [loading, data, history]);

  useEffect(() => {
    setArticleTemplates(
      printTemplates.find((item) => item.id === selectedPrintTemplateId)
        ?.printTemplate?.articleTemplates || [],
    );
  }, [selectedPrintTemplateId, printTemplates]);

  useEffect(() => {
    const articleTemplateIds = articleTemplates.map(
      (articleTemplate) => articleTemplate.id,
    );
    setVacantAssignments(
      (data?.printHeading?.assignments || []).filter(
        (assignment) =>
          !articleTemplateIds.includes(assignment.articleTemplate?.id || ''),
      ),
    );
  }, [articleTemplates, data]);

  const params: ContextProps = {
    assignments,
    setAssignments,
    vacantAssignments,
    setVacantAssignments,
    printTemplates,
    setPrintTemplates,
    selectedPrintTemplateId,
    setSelectedPrintTemplateId,
    articleTemplates,
    setArticleTemplates,
    hoveredArticleTopic,
    setHoveredArticleTopic,
    showPreview,
    setShowPreview,
    togglePreview: () => {
      setShowPreview(!showPreview);
      localStorage.setItem('showAutoPreview', String(!showPreview));
    },
    previewResponseTime,
    setPreviewResponseTime,
    isPreviewLoading,
    setIsPreviewLoading,
    getAssignment: (id: string) => assignments.find((item) => item.id === id),
    getArticleTemplate: (id: string) =>
      articleTemplates.find((item) => item.id === id),
  };

  return loading ? (
    <CircularLoader />
  ) : (
    <MultiSubjectAutoContext.Provider value={{ ...params }}>
      <AppToolbar
        childrenJustifyContent="flex-end"
        className={classes.appToolbarChildren}
        title={`Multi-Suject: ${data?.printHeading?.title}`}
        editedAt={new Date(data?.printHeading?.createdAt || '')}
        previousRoute={
          subjectSourceId
            ? {
                pathname: replaceParams(paths.SUBJECT_EDIT, {
                  id: subjectSourceId,
                }),
              }
            : { pathname: paths.SUBJECT_LIST }
        }
      />
      <MultiSubjectHeader />
      <MultiSubjectAutoForm />
    </MultiSubjectAutoContext.Provider>
  );
};
